export interface SeedGeometryLike {
  type?: string;
  coordinates?: unknown;
}

const isPosition = (value: unknown): value is GeoJSON.Position => {
  if (!Array.isArray(value) || value.length < 2) {
    return false;
  }

  return value.every(
    (coordinate: unknown) =>
      typeof coordinate === "number" && Number.isFinite(coordinate)
  );
};

const positionsEqual = (
  first: GeoJSON.Position,
  last: GeoJSON.Position
): boolean => {
  return (
    first.length === last.length &&
    first.every((coordinate, index) => coordinate === last[index])
  );
};

const isLinearRing = (value: unknown): value is GeoJSON.Position[] => {
  if (!Array.isArray(value) || value.length < 4 || !value.every(isPosition)) {
    return false;
  }

  return positionsEqual(value[0], value[value.length - 1]);
};

const isPolygonCoordinates = (
  value: unknown
): value is GeoJSON.Position[][] => {
  return Array.isArray(value) && value.length > 0 && value.every(isLinearRing);
};

const isMultiPolygonCoordinates = (
  value: unknown
): value is GeoJSON.Position[][][] => {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(isPolygonCoordinates)
  );
};

/**
 * Returns polygon coordinates without filtering, sorting, or reindexing.
 *
 * Some existing trials have geometry.type === "Point" even though their
 * coordinates are MultiPolygon-shaped. Accept that legacy representation
 * only when the coordinate nesting and rings are valid.
 *
 * A real Point such as [-59, -37] is NOT accepted.
 */
export const getSeedPolygonCoordinates = (
  geometry: SeedGeometryLike | undefined
): GeoJSON.Position[][][] => {
  if (geometry === undefined) {
    throw new Error("The plot geometry is missing.");
  }

  const coordinates = geometry.coordinates;

  if (geometry.type === "MultiPolygon" || geometry.type === "Point") {
    if (!isMultiPolygonCoordinates(coordinates)) {
      throw new Error(
        "The plot does not contain valid MultiPolygon cell coordinates."
      );
    }

    return coordinates;
  }

  if (geometry.type === "Polygon") {
    if (!isPolygonCoordinates(coordinates)) {
      throw new Error("The plot does not contain valid Polygon coordinates.");
    }

    return [coordinates];
  }

  throw new Error(
    `Unsupported plot geometry type: ${geometry.type ?? "missing"}.`
  );
};
